export interface QuizQuestionData {
  identifier: string;
  order_num: number;
  title: string;
  subtext?: string;
  background: React.ReactNode;
  subQuestions: Array<{
    text: string;
    answers: string[];
    correct_answer: number;
  }>;
  percent_correct: number;
  explanation: React.ReactNode;
  source?: string;
}

export const QUIZ_QUESTIONS: QuizQuestionData[] = [
  {
    order_num: 1,
    identifier: "the_monty_shuffle",
    title: "The Monty Shuffle",
    subtext: "Mixing things up",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're given the choice of three
          doors: Behind one door is a car; behind the others, goats. You pick a
          door. Then the host, who knows what's behind the doors, picks a random
          door with a goat behind it then opens it.{" "}
        </span>
        <span className="new-text">
          Monty then shuffles what's behind the two unopened doors — your door
          remains the same, but what's behind it may change.{" "}
        </span>
        <span className="original-text">
          He then says to you, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 2,
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 2,
      },
    ],
    percent_correct: 0.6188,
    explanation:
      "Because the two closed doors were shuffled, you lost all knowledge of what's behind them.",
  },
  {
    order_num: 2,
    identifier: "mo_goats_mo_open",
    title: "Mo Goats, Mo Open Doors",
    subtext: "Mo goats mo problems!",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're{" "}
        </span>
        <span className="new-text">given the choice of four doors: </span>
        <span className="original-text">
          Behind one door is a car; behind the others, goats. You pick a door.{" "}
        </span>
        <span className="new-text">
          Then the host, who knows what's behind the doors, picks two random
          remaining doors with goats behind them, then opens them.{" "}
        </span>
        <span className="original-text">
          He then says to you, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 0,
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 4,
      },
    ],
    percent_correct: 0.6779,
    explanation: "As long as you don’t choose the car right away, you win.",
  },
  {
    order_num: 3,
    identifier: "the_monty_fall",
    title: "The Monty Fall",
    subtext:
      "We apologize for leaving a cord out. Those responsible have been sacked.",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're given the choice of three
          doors: Behind one door is a car; behind the others, goats. You pick a
          door.{" "}
        </span>
        <span className="new-text">
          Monty then trips over a cord and randomly opens a door - which happens
          not to be your door and contain a goat.{" "}
        </span>
        <span className="original-text">
          He then says to you, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 2, // It doesn't matter
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 2, // 50%, strategy doesn’t matter
      },
    ],
    percent_correct: 0.4548,
    explanation:
      "Monty does not intentionally choose to reveal a goat, so his action does not give you additional information beyond that the car is not behind the open door. This means each of the remaining doors is equally likely to have the car, so your strategy doesn’t matter.",
    source: "https://probability.ca/jeff/writing/montyfall.pdf",
  },
  {
    order_num: 4,
    identifier: "mo_goats_mo_problems",
    title: "The Mo Goats the Mo Problems",
    subtext: "Mo goats again. This guy really likes goats.",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're{" "}
        </span>
        <span className="new-text">given the choice of four doors: </span>
        <span className="original-text">
          Behind one door is a car; behind the others, goats. You pick a door.
          Then the host, who knows what's behind the doors,{" "}
        </span>
        <span className="new-text">
          picks one random remaining door with a goat behind it, then opens it.{" "}
        </span>
        <span className="original-text">
          He then says to you, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 0,
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 1,
      },
    ],
    percent_correct: 0.5515,
    explanation:
      "You have a 75% chance of not choosing the car initially, and if so, a 50% chance of getting the car by switching. So your chance of winning is 3/4 * 1/2 = 3/8.",
  },
  {
    order_num: 5,
    identifier: "the_baaaaaad_apple",
    title: "The Baaaaaaad Apple",
    subtext: "Mind you, goat bites can be pretty nasty...",
    background: (
      <p>
        <span className="new-text">
          Monty transforms you into a goat, and you love apples! There are 3
          apples, however, 2 of them have worms. You and 2 other goats who
          appear next to you all pick separate apples. Monty, who knows which
          apples have worms, randomly selects 1 of the 2 goats (potentially
          including you) with wormy apples to take a bite. You luckily aren't
          chosen, but the goat next to you takes a bite then baaaaaacks away in
          disgust. Monty then offers to let you switch apples with the remaining
          goat.
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 2,
      },
      {
        text: "And that makes my odds of getting a wormless apple",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 2,
      },
    ],
    percent_correct: 0.3658,
    explanation:
      "The difference here is that Monty might have chosen you - to analogize to the original problem, he might have opened your door. This makes this equivalent to the Monty Fall problem from earlier.",
    source:
      "https://math.stackexchange.com/questions/65344/a-variant-of-the-monty-hall-problem",
  },
  {
    order_num: 6,
    identifier: "mo_goats_mo_picks",
    title: "The Mo Goats the Mo Picks",
    subtext: "Does this guy ever stop with the goats?",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're{" "}
        </span>
        <span className="new-text">given the choice of four doors: </span>
        <span className="original-text">
          Behind one door is a car; behind the others, goats.{" "}
        </span>
        <span className="new-text">You pick two doors. </span>
        <span className="original-text">
          Then the host, who knows what's behind all the doors,{" "}
        </span>
        <span className="new-text">
          picks one of your doors with a goat behind it, then opens it. He then
          says to you, "Do you want to open your remaining door, or switch to
          one of the others?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 1,
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 2,
      },
    ],
    percent_correct: 0.4729,
    explanation:
      "You had a 50% chance of one of your 2 doors having a car initially, and you're guaranteed to get a car if you picked one initially and stay",
  },
  {
    order_num: 7,
    identifier: "the_eccentric_billionaire",
    title: "The Eccentric Billionaire",
    subtext: "Seems like everyone here is obsessed with these goats.",
    background: (
      <p>
        <span className="new-text">
          Before you go on the show, an eccentric billionaire approaches you and
          tells you that one of the goats, with a pink ribbon, is his special
          pet goat, and he will pay you $1 billion if you win it.{" "}
        </span>
        <span className="original-text">
          You enter the game show, and you're given the choice of three doors:
          Behind one door is a car; behind the others, goats. You pick a door.{" "}
        </span>
        <span className="new-text">
          Then the host, who knows which doors have goats but knows nothing of
          this billionaire or his pet,{" "}
        </span>
        <span className="original-text">
          picks a random remaining door with a goat behind it, then opens it.{" "}
        </span>
        <span className="new-text">
          You see no pink ribbon, indicating this is not the billionaire's pet
          goat.{" "}
        </span>
        <span className="original-text">
          Monty then says to you, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 1,
      },
      {
        text: "And that makes my odds of winning the special goat",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 3,
      },
    ],
    percent_correct: 0.5732,
    explanation:
      "There's lots of potential intuitions here. A key insight is that if you pick the special goat initially, you have a 100% chance of seeing the normal goat next, where if you pick the car initially, you only have a 50% chance. So given that you saw the normal goat, it's more likely you started on the special goat.",
    source: "https://x.com/shachaf/status/1875996474913489352",
  },
  {
    order_num: 8,
    identifier: "the_tracker",
    title: "The Tracker",
    subtext:
      "We apologize again for leaving a dirty stage. Those responsible for sacking the people who have just been sacked, have been sacked.",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're given the choice of three
          doors: Behind one door is a car; behind the others, goats.{" "}
        </span>
        <span className="new-text">
          You notice tire tracks leading to the second of the three doors,
          making you believe there’s a 50% chance the car is behind that door.{" "}
        </span>
        <span className="original-text">
          Monty will ask you to pick a door. Then, knowing what's behind the
          doors, he will pick a random remaining door with a goat behind it,
          then open it. Then he'll ask, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "Initially, I should pick a door with",
        answers: ["Tracks", "No Tracks", "It doesn't matter"],
        correct_answer: 1,
      },
      {
        text: "Then after Monty reveals a goat, I should",
        answers: ["Switch", "Stay", "It doesn't matter", "It depends"],
        correct_answer: 0,
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["3/8", "1/2", "2/3", "3/4", "4/5"],
        correct_answer: 3,
      },
    ],
    percent_correct: 0.4623,
    explanation:
      "There's a 25% chance you pick the car at first, and 100% chance you win if you didn't pick the car at first, so you win in 3/4 of scenarios",
    source: "https://brilliant.org/wiki/monty-hall-problem/",
  },
  {
    order_num: 9,
    identifier: "monty_crawl",
    title: "The Monty Crawl",
    subtext: "Even Monty’s getting sick of this. We're almost done.",
    background: (
      <p>
        <span className="original-text">
          Suppose you're on a game show, and you're given the choice of three
          doors: Behind one door is a car; behind the others, goats.{" "}
        </span>
        <span className="new-text">
          You pick door 1. Monty, who is exhausted from opening so many doors,
          starts crawling starting at door 1 and opens the first door with a
          goat behind it that is not your door. This happens to be door 2.{" "}
        </span>
        <span className="original-text">
          He then says to you, "Do you want to switch doors?"
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "I should",
        answers: ["Switch", "Stay", "It doesn't matter"],
        correct_answer: 2,
      },
      {
        text: "And that makes my odds of getting a car",
        answers: ["1/3", "3/8", "1/2", "2/3", "3/4"],
        correct_answer: 2,
      },
    ],
    percent_correct: 0.4604,
    explanation:
      "50% chance of victory no matter what your strategy. If Monty had opened the 3rd door, you could guarantee victory by opening the second. But as things are, it's impossible to distinguish between the case where you picked the car initially, and the case where the car is behind door 3.",
    source: "https://probability.ca/jeff/writing/montyfall.pdf",
  },
  {
    order_num: 10,
    identifier: "the_corrupt_monty",
    title: "The Corrupt Monty",
    subtext: "You've finally broke Monty.",
    background: (
      <p>
        <span className="new-text">
          You offer Monty a bribe, and he agrees to follow whatever algorithm
          you want when he selects which door to open. However, to avoid
          suspicion, he must still follow the show’s rule: he must open a door
          with a goat behind it that is not your selected door. The ONLY thing
          you can influence is his final choice of door, and he cannot
          communicate with you in any other way. Otherwise, the game's rules
          remain the same.
        </span>
      </p>
    ),
    subQuestions: [
      {
        text: "The highest I can make my odds of a car is",
        answers: ["1/2", "2/3", "3/4", "4/5", "1/1"],
        correct_answer: 1,
      },
    ],
    percent_correct: 0.5784,
    explanation:
      "Even though you can bribe Monty to follow your algorithm, surprisingly, no algorithm can do better than the original 2/3.",
    source: "https://dynomight.net/conspiratorial-monty-hall/",
  },
].sort((a, b) => a.order_num - b.order_num);
